/**
 * useActions Hook
 *
 * Manages similar records and note/comment actions
 */

import { useState, useCallback } from 'react';
import {
  findSimilarRecords,
  addWorkNote,
  addComment,
  type RecordContext,
  type SimilarRecord,
  type ApiError,
} from '@/services/api';

interface UseActionsResult {
  // Similar Records
  similarRecords: SimilarRecord[];
  similarLoading: boolean;
  similarError: string | null;
  getSimilar: (record: RecordContext) => Promise<void>;

  // Work Notes
  noteLoading: boolean;
  noteError: string | null;
  noteSuccess: boolean;
  submitNote: (sysId: string, table: string, note: string) => Promise<boolean>;

  // Comments
  commentLoading: boolean;
  commentError: string | null;
  commentSuccess: boolean;
  submitComment: (sysId: string, table: string, comment: string) => Promise<boolean>;

  // Reset
  resetNoteStatus: () => void;
  resetCommentStatus: () => void;
}

export function useActions(): UseActionsResult {
  // Similar records state
  const [similarRecords, setSimilarRecords] = useState<SimilarRecord[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState<string | null>(null);

  // Work note state
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteSuccess, setNoteSuccess] = useState(false);

  // Comment state
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentSuccess, setCommentSuccess] = useState(false);

  /**
   * Find similar records
   */
  const getSimilar = useCallback(async (record: RecordContext) => {
    setSimilarLoading(true);
    setSimilarError(null);

    try {
      console.log(`[useActions] Finding similar records for ${record.number}`);
      const result = await findSimilarRecords(record);
      setSimilarRecords(result.records);
      console.log(`[useActions] Found ${result.count} similar records`);
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useActions] Similar error:', apiError.message);
      setSimilarError(apiError.message || 'Failed to find similar records');
    } finally {
      setSimilarLoading(false);
    }
  }, []);

  /**
   * Submit a work note
   */
  const submitNote = useCallback(async (sysId: string, table: string, note: string): Promise<boolean> => {
    setNoteLoading(true);
    setNoteError(null);
    setNoteSuccess(false);

    try {
      console.log(`[useActions] Adding work note to ${table}/${sysId}`);
      await addWorkNote(sysId, table, note);
      setNoteSuccess(true);
      console.log('[useActions] Work note added');
      return true;
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useActions] Note error:', apiError.message);
      setNoteError(apiError.message || 'Failed to add work note');
      return false;
    } finally {
      setNoteLoading(false);
    }
  }, []);

  /**
   * Submit a comment
   */
  const submitComment = useCallback(async (sysId: string, table: string, comment: string): Promise<boolean> => {
    setCommentLoading(true);
    setCommentError(null);
    setCommentSuccess(false);

    try {
      console.log(`[useActions] Adding comment to ${table}/${sysId}`);
      await addComment(sysId, table, comment);
      setCommentSuccess(true);
      console.log('[useActions] Comment added');
      return true;
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useActions] Comment error:', apiError.message);
      setCommentError(apiError.message || 'Failed to add comment');
      return false;
    } finally {
      setCommentLoading(false);
    }
  }, []);

  /**
   * Reset note status
   */
  const resetNoteStatus = useCallback(() => {
    setNoteSuccess(false);
    setNoteError(null);
  }, []);

  /**
   * Reset comment status
   */
  const resetCommentStatus = useCallback(() => {
    setCommentSuccess(false);
    setCommentError(null);
  }, []);

  return {
    similarRecords,
    similarLoading,
    similarError,
    getSimilar,
    noteLoading,
    noteError,
    noteSuccess,
    submitNote,
    commentLoading,
    commentError,
    commentSuccess,
    submitComment,
    resetNoteStatus,
    resetCommentStatus,
  };
}
